import { TiaPatientIdSystem } from '@asktia/tia-medplum-core'
import { MedplumClient } from '@medplum/core'
import {
    Bundle,
    Observation,
    DiagnosticReport,
    ServiceRequest,
    Organization,
    Practitioner
} from '@medplum/fhirtypes'
import { useMedplumContext } from '@medplum/react'
import { useQuery, UseQueryOptions } from 'react-query'
import { useUserInfo } from 'src/hooks'
import { filterObservations } from 'src/pages/MUIBased/LabResults/utils/filterObservations'
import { UserInfo } from 'src/types'

const PAGE_SIZE = 100

function getNextPageParams(bundle?: Bundle<Observation>) {
    const url = bundle?.link?.find(l => l.relation === 'next')?.url
    if (url) {
        return new URL(url).searchParams
    }
}

export type LabResultResource =
    | Observation
    | DiagnosticReport
    | ServiceRequest
    | Organization
    | Practitioner

function getLabResults(medplum: MedplumClient, user?: UserInfo) {
    return async () => {
        if (!user?.id) {
            return []
        }

        const params = new URLSearchParams()
        // Filters
        params.append(
            'patient.identifier',
            `${TiaPatientIdSystem.system}|${user.id}`
        )
        params.append('_revinclude', 'DiagnosticReport:result')
        params.append('_include:iterate', 'DiagnosticReport:based-on')
        params.append('_include:iterate', 'ServiceRequest:requester')
        params.append('_include:iterate', 'ServiceRequest:performer')
        params.append('has-member:missing', 'false')
        params.append('status:not', 'cancelled')
        // Select fields
        params.append(
            '_elements',
            'id,issued,effectiveDateTime,status,performer,hasMember,identifier,category,basedOn,result,requester,name'
        )
        // params.append(
        //     '_elements:DiagnosticReport',
        //     'id,status,code,result,basedOn'
        // )
        // Pagination
        params.append('_sort', '-date')
        params.append('_count', `${PAGE_SIZE}`)

        const results = await medplum.searchResources('Observation', params)

        let nextPageParams = getNextPageParams(results?.bundle)
        while (nextPageParams) {
            const nextPageResults = await medplum.searchResources(
                'Observation',
                nextPageParams
            )
            // @ts-ignore
            results.push(...nextPageResults)
            nextPageParams = getNextPageParams(nextPageResults?.bundle)
        }

        const filteredResults = filterObservations(results)
        return filteredResults
    }
}

export type LabResultsData = {
    observation: Observation
    diagnosticReport?: DiagnosticReport
    serviceRequest?: ServiceRequest
}

export const useGetLabResults = (
    options?: Omit<UseQueryOptions<any, any, any, any>, 'queryFn'>
) => {
    const { user, isLoading: userIsLoading } = useUserInfo()
    const { medplum, loading } = useMedplumContext()

    const { data, isLoading: labResultsLoading } = useQuery<
        LabResultResource[]
    >(['get-lab-results'], getLabResults(medplum, user), {
        cacheTime: Infinity,
        staleTime: Infinity,
        enabled: !!user,
        refetchOnWindowFocus: false,
        ...(options ?? {})
    })

    return {
        isLoading: userIsLoading || labResultsLoading || loading,
        labResults: data || []
    }
}
